import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <nav className="navbar px-6 py-4 bg-white/80 backdrop-blur-xl border-b sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo.png" alt="AutomateSEO.ai" className="h-11 w-auto" />
            <span className="text-2xl font-bold heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
          </div>
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" />Back to Home
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Privacy Policy</h1>
        <p className="text-gray-600 mb-8">Last Updated: December 7, 2025</p>
        
        <div className="prose prose-lg max-w-none space-y-8">
          <section className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Our Privacy Commitment</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              At AutomateSEO.ai, we take your privacy seriously. This policy explains how Set India Business Private Limited collects, uses, stores, and protects your personal information. We are GDPR compliant and follow industry best practices for data security.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Note:</strong> We offer a 3-day free trial for all new users. For billing and refund information, please refer to our Refund Policy. Refunds are only available within 3 days from the date of payment.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-gray-800">1.1 Information You Provide</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li><strong>Account Information:</strong> Name, email address, phone number, company name</li>
              <li><strong>Payment Information:</strong> Credit card details (processed securely by Razorpay/Stripe)</li>
              <li><strong>Profile Data:</strong> Business details, website URLs, social media accounts you connect</li>
              <li><strong>Communication Data:</strong> Support tickets, emails, chat messages with our team</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-gray-800">1.2 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li><strong>Usage Data:</strong> Features used, time spent, campaign performance</li>
              <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
              <li><strong>Cookies:</strong> Authentication tokens, preferences, analytics (see Cookie Policy)</li>
              <li><strong>Log Data:</strong> API calls, errors, system interactions for troubleshooting</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-gray-800">1.3 Third-Party Data</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Connected Accounts:</strong> Data from Google My Business, Facebook, Instagram, LinkedIn when you authorize integration</li>
              <li><strong>Analytics Data:</strong> Website traffic from Google Analytics (if connected)</li>
              <li><strong>Social Media Data:</strong> Public profile info and engagement metrics from connected platforms</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">2. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use collected information to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Provide Services:</strong> Automate SEO, create content, manage social media</li>
              <li><strong>Improve Platform:</strong> Enhance AI algorithms, fix bugs, develop features</li>
              <li><strong>Customer Support:</strong> Respond to inquiries, troubleshoot issues</li>
              <li><strong>Billing:</strong> Process payments, send invoices, manage subscriptions</li>
              <li><strong>Communications:</strong> Send service updates, feature announcements (opt-out available)</li>
              <li><strong>Security:</strong> Detect fraud, prevent abuse, ensure platform integrity</li>
              <li><strong>Legal Compliance:</strong> Meet regulatory requirements, respond to legal requests</li>
              <li><strong>Analytics:</strong> Understand usage patterns to improve user experience</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">3. Data Sharing and Disclosure</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-gray-800">3.1 We DO NOT Sell Your Data</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We never sell, rent, or trade your personal information to third parties for marketing purposes.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-gray-800">3.2 Service Providers</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We share data with trusted service providers who help us operate:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li><strong>Cloud Hosting:</strong> AWS, Google Cloud for infrastructure</li>
              <li><strong>Payment Processing:</strong> Razorpay, Stripe for secure transactions</li>
              <li><strong>AI Services:</strong> OpenAI for GPT-4, DALL-E 3, RunwayML for video generation</li>
              <li><strong>Email Services:</strong> SendGrid for transactional emails</li>
              <li><strong>Analytics:</strong> Google Analytics (anonymized data only)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-gray-800">3.3 Legal Requirements</h3>
            <p className="text-gray-700 leading-relaxed">
              We may disclose information if required by law, court order, or government regulation, or to protect our rights, users, or the public.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">4. Data Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement industry-standard security measures:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Encryption:</strong> All data transmitted using TLS/SSL encryption</li>
              <li><strong>Storage:</strong> Data encrypted at rest using AES-256</li>
              <li><strong>Access Control:</strong> Role-based access, multi-factor authentication</li>
              <li><strong>Monitoring:</strong> 24/7 security monitoring and intrusion detection</li>
              <li><strong>Audits:</strong> Regular security audits and penetration testing</li>
              <li><strong>Compliance:</strong> GDPR, ISO 27001 standard practices</li>
              <li><strong>Backups:</strong> Daily encrypted backups with 30-day retention</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">5. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Active Accounts:</strong> We retain your data as long as your account is active.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Canceled Accounts:</strong> Data retained for 30 days after cancellation (for reactivation). After 30 days, data is permanently deleted unless legal requirements mandate retention.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Backups:</strong> Backup retention for 90 days for disaster recovery purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">6. Your Rights (GDPR Compliance)</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Under GDPR and similar regulations, you have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong>Restriction:</strong> Limit how we process your data</li>
              <li><strong>Objection:</strong> Object to certain processing activities</li>
              <li><strong>Withdraw Consent:</strong> Revoke consent for data processing</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise these rights, email <strong>support@automateseo.ai</strong>. We respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">7. Cookies and Tracking</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use cookies to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Essential Cookies:</strong> Keep you logged in, remember preferences</li>
              <li><strong>Analytics Cookies:</strong> Understand usage patterns (anonymized)</li>
              <li><strong>Performance Cookies:</strong> Improve platform speed and reliability</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              You can control cookies through browser settings. Note: Disabling cookies may limit functionality. See our Cookie Policy for details.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">8. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              Our servers are located in India and may use cloud services with data centers worldwide. When we transfer data internationally, we use standard contractual clauses and ensure adequate protection per GDPR standards.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">9. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              AutomateSEO.ai is not intended for users under 18. We do not knowingly collect information from children. If you believe a child has provided us data, contact us immediately at support@automateseo.ai.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">10. Third-Party Links</h2>
            <p className="text-gray-700 leading-relaxed">
              Our platform may contain links to third-party websites. We are not responsible for their privacy practices. Review their policies before sharing information.
            </p>
          </section>

          <section className="bg-indigo-50 p-6 rounded-lg border-l-4 border-indigo-500">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">11. Social Media Platform Data Usage (Meta Platforms)</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              AutomateSEO.ai integrates with Meta Platforms including Facebook, Instagram, and WhatsApp to provide automation services such as content publishing, analytics, and messaging.
            </p>
            
            <h3 className="text-xl font-semibold mb-3 text-gray-800">Data We May Access (Only With User Permission)</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Public profile information</li>
              <li>Email address</li>
              <li>Page and Instagram business account data</li>
              <li>Messaging metadata (where applicable)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-gray-800">Our Commitment</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>We do not sell, misuse, or share user data with third parties.</strong>
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              All data access strictly follows Meta Platform Policies and is used only to provide requested services.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-gray-800">Data Deletion</h3>
            <p className="text-gray-700 leading-relaxed">
              Users can request data deletion at any time by visiting: <a href="/data-deletion" className="text-blue-600 hover:underline font-semibold">https://www.automateseo.ai/data-deletion</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">12. Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy. Significant changes will be communicated via email 30 days before taking effect. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">13. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For privacy-related questions or to exercise your rights:
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> support@automateseo.ai<br/>
              <strong>Phone:</strong> +91 9810535398<br/>
              <strong>Company:</strong> Set India Business Private Limited<br/>
              <strong>Location:</strong> India
            </p>
          </section>

          <section className="bg-green-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Your Privacy Matters</h2>
            <p className="text-gray-700 leading-relaxed">
              We're committed to transparency and protecting your data. If you have concerns or questions about how we handle your information, please don't hesitate to reach out. We're here to help.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;